import React, { useEffect, useState, useCallback } from 'react';
import { Phone, CheckCircle2, AlertCircle, Loader2, Users, Info, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SMSProvisioningPanel
 *
 * Shows the company's dedicated SMS number and key compliance stats.
 * Lets an owner/admin provision a number if one hasn't been assigned yet,
 * and send a test message to verify the channel is active.
 */
export default function DefaultSMSConfig({ userRole }) {
  const [status, setStatus]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [testSending, setTestSending]   = useState(false);
  const [testPhone, setTestPhone]       = useState('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API}/api/provisioning/sms-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (err) {
      console.error('[SMSConfig] Failed to load SMS status:', err);
      toast.error('Could not load SMS status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const token = await getAuthToken();
      const res = await axios.post(`${API}/api/provisioning/assign-number`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Number ${res.data.phoneE164} provisioned successfully`);
      fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Provisioning failed';
      toast.error(msg);
    } finally {
      setProvisioning(false);
    }
  };

  const handleTestSms = async () => {
    const digits = (s) => s.replace(/\D/g, '');
    const inputDigits = digits(testPhone.trim());
    const assignedDigits = digits(status?.phoneE164 || '');

    if (inputDigits && assignedDigits && inputDigits.slice(-10) === assignedDigits.slice(-10)) {
      toast.error('Test number must be different from your assigned SMS number');
      return;
    }

    setTestSending(true);
    try {
      const token = await getAuthToken();
      const res = await axios.post(
        `${API}/api/provisioning/test-sms`,
        { toPhone: testPhone.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Test SMS sent from ${res.data.fromNumber}`);
      fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Test SMS failed';
      toast.error(msg);
    } finally {
      setTestSending(false);
    }
  };

  const canProvision = userRole === 'owner' || userRole === 'admin';

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-gray-400 text-sm" data-testid="sms-status-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading SMS status…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="sms-provisioning-panel">

      {/* Status Card — stacks on mobile so the phone number doesn't get squeezed */}
      <div className="flex flex-col sm:flex-row items-start gap-4 p-5 rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-start gap-4 flex-1 min-w-0 w-full">
          <div className={`p-2.5 rounded-lg shrink-0 ${status?.provisioned ? 'bg-green-100' : 'bg-amber-100'}`}>
            <Phone className={`w-5 h-5 ${status?.provisioned ? 'text-green-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {status?.provisioned ? 'Dedicated SMS Number' : 'No SMS Number Assigned'}
              </h4>
              {status?.provisioned
                ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" data-testid="sms-provisioned-icon" />
                : <AlertCircle  className="w-4 h-4 text-amber-500 shrink-0" data-testid="sms-not-provisioned-icon" />
              }
            </div>

            {status?.provisioned ? (
              <p className="text-xl font-bold text-gray-800 tracking-wide whitespace-nowrap" data-testid="sms-phone-number">
                {status.phoneNumber}
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Assign a dedicated number so customers can text your business.
              </p>
            )}

            {status?.provisioned && (
              <p className="text-xs text-gray-400 mt-1">
                Provider: {status.provider === 'telnyx' ? 'Telnyx' : (status.provider || 'Telnyx')} &nbsp;·&nbsp; Status: Active
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
          {!status?.provisioned && canProvision && (
            <Button
              onClick={handleProvision}
              disabled={provisioning}
              size="sm"
              data-testid="provision-number-btn"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              className="text-white"
            >
              {provisioning
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Assigning…</>
                : 'Assign Number'
              }
            </Button>
          )}

          {status?.provisioned && canProvision && (
            <div className="flex flex-col gap-2 shrink-0 sm:items-end w-full">
              <input
                type="tel"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="Test phone (optional)"
                data-testid="test-sms-phone-input"
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full sm:w-44 focus:outline-none focus:ring-1 focus:ring-orange-300"
              />
              <Button
                onClick={handleTestSms}
                disabled={testSending}
                size="sm"
                variant="outline"
                data-testid="send-test-sms-btn"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full"
              >
                {testSending
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
                  : <><Send className="w-3.5 h-3.5 mr-1.5" />Send Test SMS</>
                }
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Stats */}
      {status?.provisioned && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            icon={<Users className="w-4 h-4 text-red-500" />}
            label="Opted-Out Contacts"
            value={String(status.optOutCount ?? 0)}
            hint="Contacts who replied STOP"
            testId="sms-opt-out-count"
          />
          <StatCard
            icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
            label="Compliance Status"
            value="Active"
            hint="STOP / UNSTOP / HELP handled"
            testId="sms-compliance-status"
          />
        </div>
      )}

      {/* Compliance Info Box */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
        <div>
          <p className="font-medium mb-0.5">Carrier Compliance</p>
          <p className="text-blue-700 text-xs leading-relaxed">
            STOP and HELP replies are handled automatically per CTIA guidelines.
            Opted-out contacts are blocked from outbound messages until they reply START.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, hint, testId }) {
  return (
    <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <p className="text-2xl font-bold text-gray-900" data-testid={testId}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
    </div>
  );
}

// ── Auth helper ───────────────────────────────────────────────────────────────

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}
